import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import RequestFullManagementAccess from "../../components/RequestFullManagementAccess/RequestFullManagementAccess";
import EventStatusControl from "../../components/EventStatusControl/EventStatusControl";
import {
  AdminAccessRequest,
  MyAdminEvent,
} from "../../api/admin";

function permissionLabel(level: string): string {
  return level === "full" ? "Full management" : "Registrations only";
}

function hasPendingRequestForEvent(
  requests: AdminAccessRequest[],
  eventId: number,
): boolean {
  return requests.some(
    (r) =>
      r.requestType !== "post_events" &&
      r.status === "pending" &&
      r.event?.id === eventId,
  );
}

interface Props {
  events: MyAdminEvent[];
  requests?: AdminAccessRequest[];
  /** When true, Edit is shown for every row; when false, only for full permission. */
  alwaysShowEdit?: boolean;
  showAccessColumn?: boolean;
  onRefresh?: () => void;
  emptyMessage?: string;
}

export default function ManagedEventsTable({
  events,
  requests = [],
  alwaysShowEdit = false,
  showAccessColumn = true,
  onRefresh,
  emptyMessage = "You are not an admin for any events yet.",
}: Props) {
  const [statusByEventId, setStatusByEventId] = useState<Record<number, string>>(
    {},
  );

  if (events.length === 0) {
    return (
      <p className="text-slate-600 text-sm border p-4 rounded-sm bg-white">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="border border-slate-200 rounded-sm overflow-x-auto bg-white">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-slate-50 text-left">
          <tr>
            <th className="p-3 font-semibold text-slate-700">Event</th>
            <th className="p-3 font-semibold text-slate-700">Status</th>
            <th className="p-3 font-semibold text-slate-700">Start date</th>
            {showAccessColumn && (
              <th className="p-3 font-semibold text-slate-700">Access</th>
            )}
            <th className="p-3" />
          </tr>
        </thead>
        <tbody>
          {events.map(
            ({
              event,
              grantedAt,
              isSuperAdmin: superFlag,
              permissionLevel,
            }) => {
              const level = permissionLevel ?? "full";
              const canEdit =
                alwaysShowEdit || superFlag || level === "full";
              const showUpgrade =
                !alwaysShowEdit &&
                !superFlag &&
                level === "registrations" &&
                !hasPendingRequestForEvent(requests, event.id);

              return (
                <tr key={event.id} className="border-t border-slate-100">
                  <td className="p-3 font-medium text-slate-900">
                    {event.title}
                  </td>
                  <td className="p-3">
                    <EventStatusControl
                      eventId={event.id}
                      status={statusByEventId[event.id] ?? event.status}
                      onUpdated={(nextStatus) => {
                        setStatusByEventId((prev) => ({
                          ...prev,
                          [event.id]: nextStatus,
                        }));
                        onRefresh?.();
                      }}
                      compact
                    />
                  </td>
                  <td className="p-3 text-slate-600">
                    {new Date(event.startDate).toLocaleDateString("en-GB")}
                  </td>
                  {showAccessColumn && (
                    <td className="p-3">
                      <span className="inline-block text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700">
                        {superFlag
                          ? "Super admin"
                          : permissionLabel(level)}
                      </span>
                      {grantedAt && !superFlag && (
                        <p className="text-xs text-slate-500 mt-1">
                          Since {grantedAt.slice(0, 10)}
                        </p>
                      )}
                    </td>
                  )}
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      {showUpgrade && onRefresh && (
                        <RequestFullManagementAccess
                          eventId={event.id}
                          eventTitle={event.title}
                          onRequestSubmitted={onRefresh}
                        />
                      )}
                      {canEdit && (
                        <Link
                          to={`/user/events/${event.id}/edit`}
                          className="text-slate-800 underline text-xs"
                        >
                          Edit
                        </Link>
                      )}
                      <NavLink
                        to={`/user/event-admin/events/${event.id}`}
                        className="text-slate-800 underline text-xs"
                      >
                        Panel
                      </NavLink>
                    </div>
                  </td>
                </tr>
              );
            },
          )}
        </tbody>
      </table>
    </div>
  );
}
